import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Heart,
  MessageCircle,
  MessageSquare,
  Phone,
  Send,
  X,
  ZoomIn,
} from "lucide-react";
import type { BannerPlacement, ClientBanner } from "@/types/api";
import { fetchClientBanners, trackBannerClick } from "@/services/news";
import { cn, resolveMediaUrl } from "@/lib/utils";

type CtaButton = "website" | "whatsapp" | "phone";

function shuffleWeighted(items: ClientBanner[]): ClientBanner[] {
  const pool: ClientBanner[] = [];
  for (const item of items) {
    const copies = Math.max(1, item.weight || 1);
    for (let i = 0; i < copies; i += 1) pool.push(item);
  }
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const seen = new Set<number>();
  const ordered: ClientBanner[] = [];
  for (const item of pool) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    ordered.push(item);
  }
  return ordered;
}

function isInternalLink(url: string) {
  return url.startsWith("/") && !url.startsWith("//");
}

function isSpecialScheme(url: string) {
  return /^(tel:|mailto:|sms:)/i.test(url);
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function BannerLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Publicidade ampliada"}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
        aria-label="Fechar imagem ampliada"
        onClick={onClose}
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[92vh] max-w-[min(96vw,1100px)] object-contain"
        onClick={(event) => event.stopPropagation()}
      />
    </div>,
    document.body,
  );
}

function resolveContactCta(banner: ClientBanner): {
  key: CtaButton;
  href: string;
  icon: ReactNode;
} | null {
  const whatsapp = (banner.cta_whatsapp_url || "").trim();
  const phone = (banner.cta_phone || "").trim();
  const website = (banner.cta_website_url || banner.link_url || "").trim();

  if (whatsapp) {
    return { key: "whatsapp", href: whatsapp, icon: <WhatsAppIcon className="h-4 w-4" /> };
  }
  if (phone) {
    return { key: "phone", href: phone, icon: <Phone className="h-4 w-4" aria-hidden /> };
  }
  if (website) {
    return {
      key: "website",
      href: website,
      icon: <MessageSquare className="h-4 w-4" aria-hidden />,
    };
  }
  return null;
}

function SponsoredPostCard({
  banner,
  variant,
  onExpandChange,
}: {
  banner: ClientBanner;
  variant: "featured" | "sidebar";
  onExpandChange?: (open: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const image = resolveMediaUrl(banner.image);
  const avatar = resolveMediaUrl(banner.advertiser_avatar || "") || null;
  const advertiser = banner.advertiser_name || banner.client_name || banner.title;
  const alt = banner.alt_text || advertiser || "Publicidade";
  const caption = (banner.caption || "").trim();
  const timeLabel = (banner.posted_ago_label || "há 2 horas").trim();
  const contact = resolveContactCta(banner);

  const setLightbox = (open: boolean) => {
    setExpanded(open);
    onExpandChange?.(open);
  };

  const trackAndOpen = (button: CtaButton, href: string) => {
    void trackBannerClick(banner.id, button).catch(() => undefined);
    if (isInternalLink(href) || isSpecialScheme(href)) {
      window.location.href = href;
      return;
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };

  if (!image) return null;

  return (
    <article
      className={cn(
        "overflow-hidden rounded-xl border border-brand-outline-variant/60 bg-brand-surface-lowest text-brand-ink shadow-sm",
        variant === "featured" ? "mx-auto max-w-md" : "w-full",
      )}
      aria-label={`Publicidade de ${advertiser}`}
    >
      <header className="flex items-center gap-3 px-3 py-2.5">
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-brand-outline-variant/50 bg-brand-surface-container">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="h-full w-full object-cover"
              width={36}
              height={36}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span
              className="flex h-full w-full items-center justify-center text-xs font-bold text-brand-primary"
              aria-hidden
            >
              {advertiser.slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-semibold">{advertiser}</p>
          <p className="text-[11px] text-brand-on-surface-variant">Publicidade</p>
        </div>
      </header>

      <div
        className={cn(
          "relative bg-brand-surface-container",
          variant === "featured" ? "aspect-square" : "aspect-[4/5]",
        )}
      >
        <img
          src={image}
          alt={alt}
          className="h-full w-full object-contain"
          width={variant === "featured" ? 640 : 400}
          height={variant === "featured" ? 640 : 500}
          loading={variant === "featured" ? "eager" : "lazy"}
          decoding="async"
        />
        <span className="absolute left-2 top-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          Anúncio
        </span>
        <button
          type="button"
          className="absolute right-2 top-2 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white shadow-sm backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          aria-label="Ampliar imagem da publicidade"
          title="Ampliar foto"
          onClick={() => setLightbox(true)}
        >
          <ZoomIn className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center gap-4 px-3 py-2 text-brand-ink" aria-hidden="true">
        <Heart className="h-5 w-5" strokeWidth={1.75} />
        <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
        <Send className="h-5 w-5" strokeWidth={1.75} />
        <Bookmark className="ml-auto h-5 w-5" strokeWidth={1.75} />
      </div>

      {caption ? (
        <p className="px-3 pb-1 text-sm leading-snug">
          <span className="font-semibold">{advertiser}</span>{" "}
          <span className="text-brand-on-surface-variant">{caption}</span>
        </p>
      ) : (
        <p className="px-3 pb-1 text-sm leading-snug">
          <span className="font-semibold">{advertiser}</span>{" "}
          <span className="text-brand-on-surface-variant">Conteúdo patrocinado no Eusébio News.</span>
        </p>
      )}

      {timeLabel ? (
        <p className="px-3 pb-2 text-[10px] uppercase tracking-wide text-brand-on-surface-variant/80">
          {timeLabel}
        </p>
      ) : null}

      <div className="space-y-2 border-t border-brand-outline-variant/40 px-3 py-3">
        {contact ? (
          <a
            href={contact.href}
            target={
              isInternalLink(contact.href) || isSpecialScheme(contact.href) ? undefined : "_blank"
            }
            rel={
              isInternalLink(contact.href) || isSpecialScheme(contact.href)
                ? undefined
                : "noopener noreferrer sponsored"
            }
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-brand-primary px-3 text-center text-xs font-semibold leading-snug text-white transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary sm:text-sm"
            aria-label={`Entrar em contato com ${advertiser}`}
            onClick={(event) => {
              event.preventDefault();
              trackAndOpen(contact.key, contact.href);
            }}
          >
            {contact.icon}
            Entrar em contato com a empresa
          </a>
        ) : null}

        <a
          href="/anuncie"
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border border-brand-primary/40 bg-brand-surface-container px-3 text-center text-xs font-semibold text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary sm:text-sm"
          aria-label="Anúncios e parcerias no Eusébio News"
        >
          Anúncios e parcerias
        </a>
      </div>

      <footer className="border-t border-brand-outline-variant/30 px-3 py-2 text-center text-[10px] text-brand-on-surface-variant">
        Conteúdo patrocinado · Eusébio News
      </footer>

      {expanded ? (
        <BannerLightbox src={image} alt={alt} onClose={() => setLightbox(false)} />
      ) : null}
    </article>
  );
}

interface ClientBannerCarouselProps {
  placement: BannerPlacement;
  variant?: "featured" | "sidebar";
  className?: string;
  intervalMs?: number;
}

export function ClientBannerCarousel({
  placement,
  variant = "featured",
  className,
  intervalMs = 8000,
}: ClientBannerCarouselProps) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["client-banners", placement],
    queryFn: () => fetchClientBanners(placement),
    staleTime: 60_000,
  });

  const slides = useMemo(() => shuffleWeighted(data ?? []), [data]);
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setIndex(0);
    setLightboxOpen(false);
  }, [slides]);

  useEffect(() => {
    if (slides.length < 2 || lightboxOpen) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [slides, intervalMs, lightboxOpen]);

  if (isPending) {
    return (
      <section
        className={cn(
          "animate-pulse rounded-xl border border-brand-outline-variant/40 bg-brand-surface-container",
          variant === "featured" ? "mx-auto h-[520px] max-w-md" : "aspect-[3/5] w-full",
          className,
        )}
        aria-hidden
      />
    );
  }

  if (isError || !slides.length) {
    return null;
  }

  const current = slides[index % slides.length];

  return (
    <section className={cn("relative", className)} aria-label="Publicidade de parceiros">
      <SponsoredPostCard
        banner={current}
        variant={variant}
        onExpandChange={setLightboxOpen}
      />

      {slides.length > 1 ? (
        <>
          <button
            type="button"
            className="absolute left-1 top-[38%] z-10 -translate-y-1/2 rounded-full bg-black/45 p-1.5 text-white hover:bg-black/65 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label="Anúncio anterior"
            onClick={() => setIndex((currentIdx) => (currentIdx - 1 + slides.length) % slides.length)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="absolute right-1 top-[38%] z-10 -translate-y-1/2 rounded-full bg-black/45 p-1.5 text-white hover:bg-black/65 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label="Próximo anúncio"
            onClick={() => setIndex((currentIdx) => (currentIdx + 1) % slides.length)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="mt-2 flex justify-center gap-1.5">
            {slides.map((slide, slideIndex) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`Ir para anúncio ${slideIndex + 1}`}
                className={cn(
                  "h-1.5 w-1.5 rounded-full transition",
                  slideIndex === index % slides.length ? "bg-brand-primary" : "bg-brand-outline-variant",
                )}
                onClick={() => setIndex(slideIndex)}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
